<template>
  <b-overlay
    :show="$store.state.app.isContentLoading"
    spinner-variant="primary"
    spinner-type="grow"
    rounded="sm"
  >

    <div>

      <!-- Filters -->
      <receipt-list-filters
        :date-filter.sync="dateFilter"
        :date-filter-value.sync="dateFilterValue"
        :clear-date.sync="clearDate"
        :branch-filter.sync="branchFilter"
        :branch-options="branchOptions"
        :reason-filter.sync="reasonFilter"
        :reason-options="reasonOptions"
        :item-filter.sync="itemFilter"
        :item-options="itemOptions"
        :staff-filter.sync="staffFilter"
        :staff-options="staffOptions"
      />

      <!-- Table Container Card -->
      <b-card
        :title="$t('Receipts')"
        class="mb-0"
      >

        <div class="mb-2 mt-2">

          <!-- Table Top -->
          <b-row>

            <!-- Per Page -->
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start pl-0 mb-1 mb-md-0"
            >
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50"
              />
            </b-col>

            <!-- Search -->
            <b-col
              cols="12"
              md="6"
            >
              <div class="d-flex align-items-center justify-content-end">
                <b-form-input
                  v-model="searchQuery"
                  class="d-inline-block mr-1"
                  :placeholder="$t('Search')"
                  @keyup.enter="search"
                />
                <b-button
                  v-if="$can('create', 'receipt')"
                  variant="primary"
                  @click="add"
                >
                  <span class="text-nowrap">{{ $t('New') }}</span>
                </b-button>
              </div>
            </b-col>
          </b-row>

        </div>

        <b-table
          ref="refReceiptListTable"
          class="position-relative"
          :items="fetchReceipts"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          empty-text="No matching records found"
          :sort-desc.sync="isSortDirDesc"
          stacked="sm"
        >

          <!-- Column: Received Reason -->
          <template #cell(received_reason)="data">
            <p>{{ data.item.received_reason != null ? data.item.received_reason.split("|")[1] : null }}</p>
            <!-- <p>{{ data.item.received_reason }}</p> -->
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <b-link :to="{ name: 'receipt-view', params: { id: data.item.id } }">
              <feather-icon
                icon="EyeIcon"
                size="16"
                class="align-middle text-body mr-1 cursor-pointer"
              />
            </b-link>
            <b-dropdown
              variant="link"
              no-caret
              :right="$store.state.appConfig.isRTL"
            >

              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>

              <b-dropdown-item
                v-if="$can('update', 'receipt')"
                :to="{ name: 'receipt-edit', params: { id: data.item.id } }"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">{{ $t('Edit') }}</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="$can('delete', 'receipt')"
              >
                <feather-icon icon="TrashIcon" />
                <span
                  class="align-middle ml-50"
                  @click="del(data.item.id)"
                >
                  {{ $t('Delete') }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>

        </b-table>
        <div class="mx-2 mb-2">
          <b-row>

            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >

              <b-pagination
                v-model="currentPage"
                :total-rows="totalReceipts"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>

          </b-row>
        </div>
      </b-card>

    </div>

  </b-overlay>
</template>

<script>
import {
  BOverlay, BCard, BRow, BCol, BFormInput, BButton, BTable,
  BDropdown, BDropdownItem, BPagination, BLink,
} from 'bootstrap-vue'
import vSelect from '@/libs/vue-select'
import store from '@/store'
import { onUnmounted } from '@vue/composition-api'
import ReceiptListFilters from './ReceiptListFilters.vue'
import storeModule from '../../../common/storeModule'
import useReceiptList from './useReceiptList'
import receiptStoreModule from '../receiptStoreModule'

export default {
  components: {
    ReceiptListFilters,
    BOverlay,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BLink,

    vSelect,
  },
  data() {
    return {
      branchOptions: [],
      staffOptions: [],
      tableColumns: [
        { key: 'id', label: this.$t('ID'), sortable: true },
        { key: 'receipt_date', label: this.$t('Date'), sortable: true },
        { key: 'receiver', label: this.$t('Receiver'), sortable: true },
        { key: 'received_reason', label: this.$t('Received Reason'), sortable: true },
        { key: 'total_amount', label: this.$t('Total Amount'), sortable: true },
        { key: 'reference', label: this.$t('Reference'), sortable: true },
        { key: 'branch_name', label: this.$t('Branch'), sortable: true },
        { key: 'actions', label: this.$t('Actions') },
      ],
    }
  },
  watch: {
    branchFilter(newR, oldR) {
      if (newR !== null) {
        console.log(newR)
        this.reasonOptions = this.reasonOptions.filter(obj => obj.branch.includes(newR))
      } else {
        console.log(oldR)
        this.reasonOptions = JSON.parse(localStorage.reasons)
      }
    },
  },
  setup() {
    const STORE_MODULE_NAME = 'receipt'

    // Register module
    if (!store.hasModule('common')) store.registerModule('common', storeModule)
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, receiptStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const clearDate = null

    const {
      fetchReceipts,
      perPage,
      currentPage,
      totalReceipts,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refReceiptListTable,
      refetchData,

      // Filters
      branchFilter,
      staffFilter,
      reasonFilter,
      itemFilter,
      dateFilter,
      dateFilterValue,
      reasonOptions,
      itemOptions,

    } = useReceiptList()

    return {

      fetchReceipts,
      perPage,
      currentPage,
      totalReceipts,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refReceiptListTable,
      refetchData,
      clearDate,
      // Extra Filters
      branchFilter,
      staffFilter,
      reasonFilter,
      itemFilter,
      dateFilter,
      dateFilterValue,
      reasonOptions,
      itemOptions,

    }
  },
  mounted() {
    this.loadBranches()
    this.loadStaffs()
  },
  methods: {
    loadBranches() {
      store
        .dispatch('common/fetchBranches')
        .then(response => {
          const { branches } = response.data.data
          for (let i = 0; i < branches.length; i += 1) {
            this.branchOptions.push({ label: branches[i].name, value: branches[i].id.toString() })
          }
        })
        .catch(error => {
          console.log(error)
        })
    },
    loadStaffs() {
      store
        .dispatch('common/fetchStaffs')
        .then(response => {
          const { staffs } = response.data.data
          for (let i = 0; i < staffs.length; i += 1) {
            this.staffOptions.push({ label: staffs[i].name, value: staffs[i].id.toString() })
          }
        })
        .catch(error => {
          console.log(error)
        })
    },
    search() {
      this.refetchData()
    },
    add() {
      this.$router.push({ name: 'receipt-new' })
    },
    del(id) {
      const that = this
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          store.dispatch('receipt/del', id)
            .then(response => {
              if (response.status === 200) {
                that.refetchData()
              } else {
                console.log(response)
              }
            })
            .catch(error => {
              window.swal('Error', JSON.stringify(error.response.data).replace(/"([^"]+)":/g, '$1:'))
            })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
